<template>
  <div>
    <div class="pa-gantt__row">
      <div
        v-for="row in innerRows"
        :id="`task-${row.id}`"
        :key="`row-${row.id}`"
        class="pa-gantt__row__inner"
        :class="{ 'pa-gantt__row__inner--milestone': !row.start || !row.end }"
        :style="getInnerRowStyle(row)"
        @click="$emit('click', row)"
      >
        <div
          v-if="!row.start || !row.end"
          class="milestone-symbol"
          :style="{ backgroundColor: getColor(row) }"
        />

        <div
          class="pa-gantt__row__background"
          :style="{ backgroundColor: getColor(row) }"
        />

        <div
          class="pa-gantt__row__progress"
          :style="{
            backgroundColor: getColor(row),
            width: `${getProperty(row, taskProgress) || 0}%`
          }"
        />

        <span
          class="pa-gantt__row__task-name text-truncate"
          :style="{ marginLeft: (!row.start || !row.end) ? '50px' : null }"
        >
          {{ getProperty(row, taskName) }}
        </span>
      </div>
    </div>

    <!-- Children container -->
    <v-expand-transition v-if="task.children && task.children.length && !groupBy">
      <div v-show="task.expanded">
        <pa-gantt-row
          v-for="child in task.children"
          :key="`row-${child.id}`"
          :gantt-start="ganttStart"
          :grid-width="gridWidth"
          :task="child"
          :task-name="taskName"
          :task-progress="taskProgress"
          :zoom-level="zoomLevel"
          @click="$emit('click', child)"
        />

        <div class="pa-gantt__add-task pa-gantt__add-task--dummy">
&nbsp;
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<script>
import { getProperty } from '@/utils'

export default {
  name: 'PaGanttRow',
  props: {
    ganttStart: {
      type: Object,
      required: true
    },
    gridWidth: {
      type: Number,
      required: true
    },
    groupName: {
      type: String,
      default: 'group'
    },
    groupBy: {
      type: String,
      default: null
    },
    task: {
      type: Object,
      required: true
    },
    taskName: {
      type: String,
      required: true
    },
    taskProgress: {
      type: String,
      required: true
    },
    zoomLevel: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      innerRows: [],
      oneDayMs: 24 * 60 * 60 * 1000
    }
  },
  watch: {
    task () {
      this.buildInnerRows()
    }
  },
  mounted () {
    this.buildInnerRows()
  },
  methods: {
    getProperty,
    getColor (task) {
      if (task.progress === 100) {
        // complete
        return '#66BB6A'
      } else if (task.end < new Date() ||
        (!task.end && task.start < new Date())) {
        // overdue
        return '#fa8072'
      } else {
        // on time
        return '#2196F3'
      }
    },
    buildInnerRows () {
      if (this.groupBy) {
        this.innerRows = [ this.task, ...this.task.children ]
      } else {
        this.innerRows = [ this.task ]
      }
      this.innerRows = this.innerRows.filter(row => row.start || row.end)
    },
    getInnerRowStyle (row) {
      const style = {}

      if (row.start && row.end) {
        // regular task with start and end
        style.width = `${row.end.diff(row.start, this.zoomLevel, true) * this.gridWidth}px`
        const offset = row.start.diff(this.ganttStart, this.zoomLevel, true)
        style.left = `${offset * this.gridWidth}px`
      } else if (!row.start && !row.end) {
        // no date at all
      } else {
        // milestone
        const offset = (row.start || row.end).diff(this.ganttStart, this.zoomLevel, true)
        style.left = `${offset * this.gridWidth}px`
      }

      return style
    }
  }
}
</script>

<style lang="scss">
@import "~styles/_vars.scss";

.milestone-symbol {
  position: absolute;
  height: 40px;
  width: 40px;
  transform: translateX(5px) scaleX(.6) rotateZ(45deg) scale(.7);
  border-radius: 4px;
}

.pa-gantt__row {
  height: $pa-gantt-task-height;
  position: relative;

  &__inner {
    position: absolute;
    top: 4px;
    height: $pa-gantt-task-height - 8px;
    width: 0;
    border-radius: 2px;
    transition: all 0.4s;
    background: white;
    cursor: pointer;
  }

  &__task-name {
    position: sticky;
    left: 1em;
    color: white;
    line-height: $pa-gantt-task-height - 8px;
    margin-left: 1em;
  }

  &__background {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    opacity: .6;
  }

  &__progress {
    position: absolute;
    height: 100%;
    width: 0;
    border-top-left-radius: 2px;
    border-bottom-left-radius: 2px;
    transition: all 0.4s;
  }

  &--complete {
    background: #66BB6A;
  }

  &--ontime {
    background: #2196F3;
  }

  &--overdue {
    background: #fa8072;
  }
}
</style>
